// import React from "react";
// import ReactDOM from "react-dom/client";
// import App from "./App.jsx";

// ReactDOM.createRoot(document.getElementById("root")).render(
//   <React.StrictMode>
//     <App />
//   </React.StrictMode>
// );

import React from "react";
import ReactDOM from "react-dom/client";
import { RouterProvider } from "@tanstack/react-router";
import { ThemeProvider } from "@mui/material/styles";
import { theme } from "./styles/theme";
import { ErrorBoundary } from "./components/ErrorBoundary";

import { router } from "./router";
import "@fontsource/raleway/300.css";
import "@fontsource/raleway/400.css";
import "@fontsource/raleway/500.css";
import "@fontsource/raleway/600.css";
import "@fontsource/raleway/700.css";

// MSAL imports
import { EventType } from "@azure/msal-browser";
import { msalInstance } from "./msalInstance";
import { CustomNavigationClient } from "./utils/NavigationClient";

// Every one of these must be set or MSAL is constructed with an undefined
// clientId and an undefined authority.
// Referenced statically so Vite can inline them at build time.
// The reset authority is deliberately absent: only B2C has one.
const requiredEnv = {
  VITE_API_URI: import.meta.env.VITE_API_URI,
  VITE_AUTH_CLIENT_ID: import.meta.env.VITE_AUTH_CLIENT_ID,
  VITE_AUTH_AUTHORITY: import.meta.env.VITE_AUTH_AUTHORITY,
  VITE_AUTH_KNOWN_AUTHORITIES: import.meta.env.VITE_AUTH_KNOWN_AUTHORITIES,
  VITE_AUTH_SCOPES: import.meta.env.VITE_AUTH_SCOPES,
};

/**
 * The whole app mounts inside the MSAL bootstrap promise below, so anything
 * that throws there leaves a blank page and, because the rejection was never
 * handled, often nothing useful in the console either. Say what went wrong.
 *
 * Built with textContent rather than innerHTML - `detail` can contain values
 * from the environment, and this must not become an injection point.
 */
const showBootstrapError = (heading, detail) => {
  console.error(`${heading}\n\n${detail}`);

  const container = document.getElementById("root");
  if (!container) return;

  const panel = document.createElement("div");
  panel.style.cssText =
    "font-family:system-ui,sans-serif;max-width:52rem;margin:4rem auto;padding:1.5rem 2rem;" +
    "border:1px solid #d32f2f;border-radius:8px;color:#1e1e1e";

  const title = document.createElement("h1");
  title.style.cssText = "font-size:1.25rem;margin:0 0 .75rem;color:#d32f2f";
  title.textContent = heading;

  const body = document.createElement("pre");
  body.style.cssText =
    "white-space:pre-wrap;word-break:break-word;margin:0;font-size:.875rem;line-height:1.6";
  body.textContent = detail;

  panel.append(title, body);
  container.replaceChildren(panel);
};

const renderApp = () => {
  // Default to using the first account if no account is active on page load
  if (
    !msalInstance.getActiveAccount() &&
    msalInstance.getAllAccounts().length > 0
  ) {
    // Account selection logic is app dependent. Adjust as needed for different use cases.
    msalInstance.setActiveAccount(msalInstance.getAllAccounts()[0]);
  }

  // Optional - This will update account state if a user signs in from another tab or window
  msalInstance.enableAccountStorageEvents();

  msalInstance.addEventCallback((event) => {
    if (
      event.eventType === EventType.LOGIN_SUCCESS ||
      event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
      event.eventType === EventType.SSO_SILENT_SUCCESS
    ) {
      const account = event.payload.account;
      msalInstance.setActiveAccount(account);
    }
  });

  // MSAL navigates between pages by handing a URL to its navigation client.
  // Push those through the router's history so they stay client-side. The
  // history API takes a raw href, so MSAL's query strings survive without
  // having to be matched against a route pattern first.
  //
  // Under react-router this had to live in a component (useNavigate is a
  // hook), which forced a wrapper that skipped its own first render. The
  // TanStack router exists outside React, so it can simply be handed the
  // instance here, before anything mounts.
  msalInstance.setNavigationClient(
    new CustomNavigationClient((path, options = {}) =>
      options.replace
        ? router.history.replace(path)
        : router.history.push(path),
    ),
  );

  const container = document.getElementById("root");
  const root = ReactDOM.createRoot(container);

  root.render(
    <React.StrictMode>
      <ThemeProvider
        theme={theme}
        defaultMode="light"
        disableTransitionOnChange
      >
        <ErrorBoundary>
          <RouterProvider router={router} />
        </ErrorBoundary>
      </ThemeProvider>
    </React.StrictMode>,
  );
};

const missingEnv = Object.keys(requiredEnv).filter((key) => !requiredEnv[key]);

if (missingEnv.length > 0) {
  showBootstrapError(
    "Missing environment configuration",
    `The following variables are not set:\n\n` +
      missingEnv.map((key) => `  - ${key}`).join("\n") +
      `\n\nCopy .env.example to .env, fill in the values, then restart the dev ` +
      `server (Vite only reads .env files at startup).`,
  );
} else {
  msalInstance
    .initialize()
    .then(renderApp)
    .catch((error) => {
      showBootstrapError(
        "Authentication failed to initialise",
        `The app could not start because MSAL threw during initialisation.\n\n${
          error?.stack || error?.message || String(error)
        }`,
      );
    });
}
